import React from 'react';
import Table, { ColumnConfig } from 'components/table';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from 'components/ui/tooltip';
import NoSummaryFound from 'components/summary-tab/no-summary-found';
import { PAYMENTS } from 'state/payment/type';
import CURRENCY from 'utils/currency';
import { getPercentChange } from 'utils/dashboard';
import { AmountChangePercentLabel } from 'utils/status-formatter';
import { formatDate } from 'utils/date-format';

interface TotalTipSummaryProps {
  summary_data: PAYMENTS.ISummaryData;
  previousStartDate: string;
  previousEndDate: string;
}

interface TipsSummaryTotalsRow {
  text: string;
  totalTip: number;
  numberOfTips: number;
  averageTipAmount: number;
}

const PercentChangeWithTooltip = ({
  value,
  tooltipText,
}: {
  value: number | null;
  tooltipText: string;
}) => {
  if (value === null) return null;
  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>
          <span className="ml-2 inline-flex">
            <AmountChangePercentLabel value={value} />
          </span>
        </TooltipTrigger>
        <TooltipContent side="bottom">{tooltipText}</TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
};

const TotalTipSummary = ({
  summary_data,
  previousStartDate,
  previousEndDate,
}: TotalTipSummaryProps): JSX.Element => {
  const tooltipText = `Compared to previous period: ${formatDate(
    previousStartDate,
    'MMM dd yyyy, hh:mm a'
  )} - ${formatDate(previousEndDate, 'MMM dd yyyy, hh:mm a')}`;

  const totalTipPercentChange = getPercentChange(
    summary_data.total_tip,
    summary_data.previous_total_tip
  );
  const numberOfTipPercentChange = getPercentChange(
    summary_data.number_of_tips,
    summary_data.previous_number_of_tips
  );
  const averageOfTipPercentChange = getPercentChange(
    summary_data.average_tip_amount,
    summary_data.previous_average_tip_amount
  );

  const products: TipsSummaryTotalsRow[] = [
    {
      text: 'TOTALS',
      totalTip: summary_data.total_tip,
      numberOfTips: summary_data.number_of_tips,
      averageTipAmount: summary_data.average_tip_amount,
    },
  ];

  const columns: ColumnConfig<TipsSummaryTotalsRow>[] = [
    {
      key: 'text',
      label: '',
      renderCell: (row) => (
        <div className="font-medium text-sm text-gray-blue-80">
          {row.text ? row.text.toUpperCase() : ''}
        </div>
      ),
    },
    {
      key: 'totalTip',
      label: 'Tips',
      renderCell: (row) => (
        <div className="flex items-center text-left text-sm font-medium text-gray-blue-80">
          {row.totalTip ? CURRENCY.convertToMainUnit(row.totalTip) : '$0.00'}
          <PercentChangeWithTooltip
            value={totalTipPercentChange}
            tooltipText={tooltipText}
          />
        </div>
      ),
    },
    {
      key: 'numberOfTips',
      label: 'No of Tips',
      renderCell: (row) => (
        <div className="flex items-center text-left text-sm font-medium text-gray-blue-80">
          {row.numberOfTips}
          <PercentChangeWithTooltip
            value={numberOfTipPercentChange}
            tooltipText={tooltipText}
          />
        </div>
      ),
    },
    {
      key: 'averageTipAmount',
      label: 'Average Tip Amount',
      renderCell: (row) => (
        <div className="flex items-center text-left text-sm font-medium text-gray-blue-80">
          {row.averageTipAmount
            ? CURRENCY.convertToMainUnit(row.averageTipAmount)
            : '$0.00'}
          <PercentChangeWithTooltip
            value={averageOfTipPercentChange}
            tooltipText={tooltipText}
          />
        </div>
      ),
    },
  ];

  return (
    <Table
      id="totalTipSummary"
      data={products}
      columns={columns}
      theme={{
        showTableBorder: true,
        tableGridLayout: '1.6fr 1fr 1fr 1fr',
      }}
      customTheme={{
        HeaderCell: `height: 48px; background-color: #fff !important;`,
        Cell: `height: 64px; background-color: #f7f8f8;`,
        Table: `border-radius: 4px;`,
        Row: `
          & > td:first-child {
            border-bottom-left-radius: 4px;
          }
          & > td:last-child {
            border-bottom-right-radius: 4px;
          }
        `,
      }}
      noDataIndication={<NoSummaryFound />}
    />
  );
};

export default TotalTipSummary;
